import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useAppNavigation } from 'contexts/navigation';
import { useProjectId } from 'store/project';
import type { IssueWorkflowOutDto } from 'shared/domain/workflow/types';
import { stableSort } from 'helpers/sort';
import { useWorkflows } from 'store/workflow';
import { useProcessByWorkflowId } from 'store/process';
import { WorkflowConfigurationPresentational } from './presentational';
import type { WorkflowConfigurationPresentationalProps } from './types';

function WorkflowConfiguration(): ReactElement {
  const navigation = useAppNavigation();
  const projectId = useProjectId();

  const { workflows, loading, createWorkflow, reload: reloadWorkflows } = useWorkflows();
  const workflowToProcess = useProcessByWorkflowId();

  useEffect(() => {
    void reloadWorkflows();
  }, [reloadWorkflows]);

  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createLoading, setCreateLoading] = useState(false);

  const sortedWorkflows = useMemo(
    () =>
      stableSort([...workflows], (a, b) => {
        const nameA = a.name ?? '';
        const nameB = b.name ?? '';
        return nameA.localeCompare(nameB);
      }),
    [workflows]
  );

  const resetCreate = useCallback((): void => {
    setCreateOpen(false);
    setCreateName('');
  }, []);

  const handleSubmitCreate = useCallback(async (): Promise<void> => {
    const name = createName.trim();
    if (!name || !projectId || createLoading) return;
    setCreateLoading(true);
    try {
      const workflow = await createWorkflow.execute({ name }, projectId);
      resetCreate();
      navigation.goToWorkflowCreator(projectId, workflow._id);
    } catch {
      //
    } finally {
      setCreateLoading(false);
    }
  }, [createName, projectId, createLoading, createWorkflow, resetCreate, navigation]);

  const navigateToWorkflowCreator = useCallback(
    (w: IssueWorkflowOutDto): void => {
      if (!projectId) return;
      navigation.goToWorkflowCreator(projectId, w._id);
    },
    [navigation, projectId]
  );

  const props: WorkflowConfigurationPresentationalProps = {
    projectId,
    sortedWorkflows,
    workflowToProcess,
    loadingList: loading,
    createOpen,
    createName,
    createWorkflow,
    createLoading,
    onOpenCreate: () => setCreateOpen(true),
    onCloseCreate: resetCreate,
    onChangeCreateName: setCreateName,
    onSubmitCreate: () => void handleSubmitCreate(),
    onNavigateToWorkflowCreator: navigateToWorkflowCreator,
  };

  return <WorkflowConfigurationPresentational {...props} />;
}

export const MemoWorkflowConfiguration = React.memo(WorkflowConfiguration);
export { WorkflowEditorPage } from './wizard/edit';
